import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { store, RootState } from ".";
import { isAfter } from "date-fns";
import { GroupApi, GroupServiceGetManyResponse } from "../services/api";
import { useApiConfiguration } from "../services/api-configuration";
import { APIStatus, GroupStatus } from "../utils/types";
import { groupIsAssignedToCurrentUser, isGroupCompleted } from "../utils/groupList";
import { UserRole } from "./auth";
import { assignGroup, markGroupAsComplete } from "./group";

export enum IGroupSort {
  NAME_DESC = "NAME_DESC",
  EFFECTIVE_DATE_ASC = "EFFECTIVE_DATE_ASC",
  EFFECTIVE_DATE_DESC = "EFFECTIVE_DATE_DESC",
  REQUESTED_DATE_ASC = "REQUESTED_DATE_ASC",
  REQUESTED_DATE_DESC = "REQUESTED_DATE_DESC",
  REISSUE_MONTH_ASC = "REISSUE_MONTH_ASC",
  REISSUE_MONTH_DESC = "REISSUE_MONTH_DESC",
}

export type State = {
  globalSearchString: string;
  groups: GroupServiceGetManyResponse[];
  filters: {
    searchString: string;
    currentSortOrder: IGroupSort;
    status: string | null;
    requestStatus: string[];
    allFiltersSelected: boolean;
    currentPage: number;
    perPage: number;
  };
  apiState: APIStatus | null;
}

export type Actions = {
  toggleRequestStatusFilter: {
    value: string;
    role: UserRole | null;
  };
  setStatusFilter: string | null;
  setSearchString: string;
  setSortOrder: State['filters']['currentSortOrder'];
}

function getAllStatusFilters(role: UserRole): string[] {  
  return [
    'requested_by_am',
    'approved_by_actuarial',
    'declined_by_actuarial',
    ...role === UserRole.ACTUARY ? [
        'assigned_to_me',
        'unassigned',
    ] : [],
  ];
}

const initialState: State = {
    globalSearchString: '',
    apiState: null,
    groups: [],
    filters: {
      searchString: '',
      allFiltersSelected: false,
      status: null,
      requestStatus: [],
      currentSortOrder: IGroupSort.NAME_DESC,
      currentPage: 1,
      perPage: 10,
    }
}

export const fetchGroups = createAsyncThunk(
  'groups/fetchAll',
  async () => {
    const api = new GroupApi(useApiConfiguration());
    const response = await api.apiGroupGetGroupsWithOpenRequestsGet();
    return response;
  },
)

export const groupListStore = createSlice({
    name: 'groupList',
    initialState,
    reducers: {
      setGlobalSearchString: (state: State, action: { payload: string }) => {
        state.globalSearchString = action.payload;
      },
      setCurrentPage: (state: State, action: { payload: number }) => {
        state.filters.currentPage = action.payload;
      },
      setSortOrder: (state: State, action: { payload: Actions['setSortOrder']}) => {
        state.filters.currentSortOrder = action.payload;
      },
      setSearchString: (state: State, action: { payload: Actions['setSearchString'] }) => {
        state.filters.searchString = action.payload;
      },
      setStatusFilter: (state: State, action: { payload: Actions['setStatusFilter']}) => {
        state.filters.status = action.payload;
      },
      toggleRequestStatusFilter: (state: State, action: { payload: Actions['toggleRequestStatusFilter']}) => {
        if (action.payload.value === 'all') {
          if (!state.filters.allFiltersSelected) {
            state.filters.allFiltersSelected = true;
            state.filters.requestStatus = getAllStatusFilters(action.payload.role);
          } else {
            state.filters.allFiltersSelected = false;
            state.filters.requestStatus = [];
          }
        } else {
          if (state.filters.requestStatus.includes(action.payload.value)) {
            state.filters.requestStatus = state.filters.requestStatus.filter((status) => status !== action.payload.value);
          } else {
            state.filters.requestStatus = [
              ...state.filters.requestStatus,
              action.payload.value,
            ]
          }
        }

        state.filters.allFiltersSelected = state.filters.requestStatus.length === getAllStatusFilters(action.payload.role).length;
      },
    },
    extraReducers: (builder) => {
      builder.addCase(fetchGroups.fulfilled, (state, action) => {
        state.apiState = APIStatus.COMPLETE;
        state.groups = action.payload;
      }),

      builder.addCase(fetchGroups.pending, (state) => {
        state.apiState = APIStatus.LOADING;
        state.groups = [];
      });

      builder.addCase(fetchGroups.rejected, (state) => {
        state.apiState = APIStatus.ERROR;
        state.groups = [];
      });

      builder.addCase(assignGroup.fulfilled, (state, action) => {
        state.groups = state.groups.map((group) => {
          if (group.policyNumber === action.payload.policyDetailModel?.policyNumber) {
            return {
              ...group,
              assignedTo: action.payload.assignedTo,
            }
          }

          return group;
        });
      });

      builder.addCase(markGroupAsComplete.fulfilled, (state, action) => {
        state.groups = state.groups.map((group) => {
          if (group.policyNumber === action.payload.policyDetailModel?.policyNumber) {
            return {
              ...group,
              requestedBy: action.payload.requestedBy,
              requestStatus: action.payload.requestStatus,
              requestedDate: action.payload.requestedDate,
            }
          }

          return group;
        });
      })
    }
});

export const {
  setGlobalSearchString,
  setSearchString,
  setCurrentPage,
  setSortOrder,
  setStatusFilter,
  toggleRequestStatusFilter,
} = groupListStore.actions;

export function selectPaginatedAndFilteredGroups({ groups, filters }: RootState['groupList']) {
  const firstItemIndex = (filters.currentPage - 1) * filters.perPage;

  return selectFilteredGroups({ groups, filters }).
    slice(firstItemIndex, firstItemIndex + filters.perPage);
}

export function selectFilteredGroups({ groups, filters }: { groups: GroupServiceGetManyResponse[], filters?: Partial<State['filters']> }) {
  const { auth: { role, user } } = store.getState();

  function groupRequestBelongsToCurrentUser(group: GroupServiceGetManyResponse) {
    if (role === UserRole.ACTUARY) return true;
    if (!user?.profile?.email) return true;
    
    return group.requestedBy === user?.profile.email;
  }

  function isRequestStatusMatch(statuses: string[], group: GroupServiceGetManyResponse) {
    if (statuses.length) {
      return statuses.some((status) => {
        switch (status) {
          case 'unassigned':
            return !group.assignedTo;
          case 'assigned_to_me':
            return groupIsAssignedToCurrentUser(group, user);
          case 'requested_by_am':
            return [
              GroupStatus.RERATE_REQUESTED,
              GroupStatus.TIER2_REQUESTED,
              GroupStatus.HIGH_RISK_REQUESTED,
            ].includes(group.requestStatus as GroupStatus);
          case 'approved_by_actuarial':
            return [
              GroupStatus.TIER2_APPROVED,
              GroupStatus.TIER2_COMPLETED,
              GroupStatus.RERATE_COMPLETED,
            ].includes(group.requestStatus as GroupStatus);
          case 'declined_by_actuarial':
            return [
              GroupStatus.TIER2_DECLINED,,
            ].includes(group.requestStatus as GroupStatus);
        }
      });
    }

    return true;
  }

  function isStatusMatch(status: string | null | undefined, group: GroupServiceGetManyResponse) {
    if (role === UserRole.ACTUARY && !status && isGroupCompleted(group)) {
      return false;
    }

    if (status) {
      if (status === GroupStatus.COMPLETED) {
        // TODO: check if group has been marked as complete if user is an AM
        if (role === UserRole.ACCOUNT_MANAGER) {
          return group.requestStatus === GroupStatus.COMPLETED;
        }

        return group.requestStatus === GroupStatus.COMPLETED || isGroupCompleted(group);
      }
      else {
        if (role === UserRole.ACTUARY) {
          return !isGroupCompleted(group) && group.requestStatus?.toUpperCase().startsWith(status.toUpperCase());
        }

        return group.requestStatus !== GroupStatus.COMPLETED && group.requestStatus?.toUpperCase().startsWith(status.toUpperCase());
      }
    } else {
      return ![GroupStatus.NO_REQUESTS, GroupStatus.COMPLETED].includes(group.requestStatus as GroupStatus)
    }
  }

  function isPolicyNumberMatch(searchString: string | undefined, group: GroupServiceGetManyResponse) {
    if (searchString) {
      return searchString && !!group.policyNumber?.toUpperCase()?.includes(filters.searchString?.toUpperCase());
    }
    return true;
  }

  if (!filters) {
    return groups;
  }

  return [...groups].
    filter((group: GroupServiceGetManyResponse) => {
      return (
        (!filters.searchString || isPolicyNumberMatch(filters.searchString, group)) &&
        (!filters.requestStatus || isRequestStatusMatch(filters.requestStatus, group)) &&
        isStatusMatch(filters.status, group)
        //  &&
        // groupRequestBelongsToCurrentUser(group)
      );
    }).
    sort((a: GroupServiceGetManyResponse, b: GroupServiceGetManyResponse) => {
      switch (filters.currentSortOrder) {
        case IGroupSort.EFFECTIVE_DATE_ASC:
          return isAfter(a.originalEffectiveDate!, b.originalEffectiveDate!) ? 1 : -1;
        case IGroupSort.EFFECTIVE_DATE_DESC:
          return isAfter(a.originalEffectiveDate!, b.originalEffectiveDate!) ? -1 : 1;
        case IGroupSort.REISSUE_MONTH_ASC:
          return isAfter(a.reissueDate!, b.reissueDate!) ? 1 : -1;
        case IGroupSort.REISSUE_MONTH_DESC:
          return isAfter(a.reissueDate!, b.reissueDate!) ? -1 : 1;
        case IGroupSort.REQUESTED_DATE_ASC:
          return isAfter(a.requestedDate!, b.requestedDate!) ? 1 : -1;
        case IGroupSort.REQUESTED_DATE_DESC:
          return isAfter(a.requestedDate!, b.requestedDate!) ? -1 : 1;
        case IGroupSort.NAME_DESC:
        default:
          return a.groupName!.localeCompare(b.groupName!);
      }
    });
}

export default groupListStore.reducer;